import React, {useState, useEffect, useCallback, useMemo} from 'react';
import {useSelector} from 'react-redux';
import MobilePopup from '../../../../component/newPopup/mobilePopup/MobilePopup';
import {EventsTable, CreateEventPopup, HeaderComponent, SearchFilterComponent, FooterComponent} from './components';
import {useEventFilters} from './hooks/useEventFilters';
import {useEventSelection} from './hooks/useEventSelection';
import * as css from './AssigningEventsPop.css';
import {AssigningEventsPopProps, StatusFilter, CategoryFilter} from './utils/types';
import {TEvent} from '../../../../redux/event/event.type';
import useShow from '../../../../hooks/useShow';
import {getSubscriptionEvents} from '../../../../redux/subscription/subscriptionActions';
import {TState} from '../../../../redux/store.type';
import {assignEventsToPlan, getPlanEvents} from '../../../../redux/subscription/subscriptionActions';
import setGlobalState from '../../../../redux/setGlobalState';

function AssigningEventsPop({planTid, show, onClose, subscriptionTitle, subscriptionSubtitle, showEventKind = false}: AssigningEventsPopProps) {
    const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
    const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('all');
    const [searchFilteredEvents, setSearchFilteredEvents] = useState<TEvent[]>([]);
    const [lastOffset, setLastOffset] = useState<number>(-1);
    const createEventShow = useShow();
    
    const subscriptionEvents = useSelector((s: TState) => s.subscription.events);
    const getSubscriptionEventsXHR = useSelector((s: TState) => s.subscription.getSubscriptionEventsXHR);
    // Use custom hooks for filtering and selection logic
    // events with no duplicates eid
    const events = useMemo(() => {
        return subscriptionEvents.filter((event, index, self) =>
            index === self.findIndex((t) => t.eid === event.eid)
        );
    }, [subscriptionEvents]);
    const {baseFilteredEvents, activeEvents} = useEventFilters({
        events: events ?? [],
        statusFilter,
        categoryFilter
    });

    const {selectedEvents, mandatoryCount, optionalCount, allActiveSelected, handleEventSelect, handleEventKindChange, handleSelectAllActive} = useEventSelection({activeEvents});

    useEffect(() => {
        setSearchFilteredEvents(baseFilteredEvents);
    }, [baseFilteredEvents]);

    useEffect(() => {
        const currentOffset = subscriptionEvents?.length ?? 0;
        const loadEvents = () => {
            if(getSubscriptionEventsXHR !== 'LOADING' && lastOffset !== currentOffset) {
                setLastOffset(currentOffset);
                getSubscriptionEvents({limit: 10, offset: currentOffset}).then();
            }
        };
        if(lastOffset === -1) {
            setLastOffset(currentOffset);
            getPlanEvents(planTid).then(res => {
                if(res?.events?.length > 0) {
                    res?.events.forEach((event: any) => {
                        handleEventSelect(event.eid, true);
                    });
                } else {
                    loadEvents();
                }
            });
        } else {
            loadEvents();
        }
    }, [planTid, subscriptionEvents?.length, getSubscriptionEventsXHR]);

    useEffect(() => {
        return () => {
            setGlobalState('subscription.events', []);
            setLastOffset(-1);
        };
    }, []);
    const handleSave = useCallback(() => {
        assignEventsToPlan(planTid, selectedEvents.map(event => event.eid)).then(() => {});
        onClose();
    }, [selectedEvents]);

    return (
        <>
            <css.assigningEventsPop>
                <MobilePopup 
                    showClass={show.showClass}
                    close={onClose}
                    title=""
                    dataCyName="assigningEvents"
                    list={undefined} 
                    forwardRef={undefined}
                    height={'90vh'}
                >
                    <>
                        <css.container>
                            <HeaderComponent
                                planEventsCount={selectedEvents.length}
                                mandatoryCount={mandatoryCount}
                                optionalCount={optionalCount}
                                onCreateEvent={createEventShow.open}
                                subscriptionTitle={subscriptionTitle}
                                subscriptionSubtitle={subscriptionSubtitle}
                                onClose={onClose}
                                showEventKind={showEventKind}
                            />
                            <SearchFilterComponent
                                statusFilter={statusFilter}
                                categoryFilter={categoryFilter}
                                baseFilteredEvents={baseFilteredEvents}
                                onStatusFilterChange={setStatusFilter}
                                onCategoryFilterChange={setCategoryFilter}
                                onFilteredEventsChange={setSearchFilteredEvents}
                            />
                            <EventsTable 
                                events={searchFilteredEvents}
                                selectedEvents={selectedEvents}
                                onEventSelect={handleEventSelect}
                                onEventKindChange={handleEventKindChange}
                                loading={getSubscriptionEventsXHR}
                                showEventKind={showEventKind}
                            />
                        </css.container>
                        <FooterComponent
                            allActiveSelected={allActiveSelected}
                            onSelectAllActive={handleSelectAllActive}
                            onClose={onClose}
                            onSave={handleSave}
                        />
                    </>
                </MobilePopup> 
            </css.assigningEventsPop>
            {createEventShow.isOpen && (
                <CreateEventPopup 
                    show={createEventShow}
                />
            )}
        </>
    );
}

export default AssigningEventsPop;
